import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormFieldTextarea,
  FormFieldSelect,
  DateOnlyPicker,
} from '@/shared/components/custom/form-field';
import { EstimatedPicker } from '@/features/tasks/public';
import type { ProjectStatus } from '@/features/tasks/public';
import type { Tag as ProjectTag } from '@/features/tags/public';
import { AI_PRIORITIES } from '../schemas';
import type { QuickTaskDraftFormValues } from '../schemas';
import type { AiFieldKey, ProposalCandidate } from '../types';
import { TaskFieldCandidateSelect } from './TaskFieldCandidateSelect';
import { TaskFieldStatusEditor } from './TaskFieldStatusEditor';
import { TaskFieldTagsEditor } from './TaskFieldTagsEditor';
import type { IdLabelOption } from '../hooks/useBoardContext';

/** Label maps backing the chip editors' selects. */
export interface TaskFieldOptions {
  statuses: IdLabelOption[];
  groups: IdLabelOption[];
  members: IdLabelOption[];
  tags: IdLabelOption[];
}

/** Every field the draft schema holds, which is every field a chip can edit. */
export type FormFieldKey = AiFieldKey;

interface TaskFieldEditorProps {
  field: FormFieldKey;
  candidates: ProposalCandidate[];
  control: Control<QuickTaskDraftFormValues>;
  options: TaskFieldOptions;
  projectId: string;
  boardId: string;
  projectStatuses: ProjectStatus[];
  selectedTags: ProjectTag[];
  onTagsChange: (tags: ProjectTag[]) => void;
  /** Testid namespace, matching the chip that opens this editor. */
  testIdPrefix?: string;
  /**
   * The value the board does not have yet, when the proposal named one. Every control here picks
   * from what exists, so without this a proposed new status or group opened an editor showing
   * nothing at all — the pill named a value the editor could not account for.
   */
  pending?: { label: string; color: string | null } | null;
}

/**
 * The editor a task-field chip opens, shared by both quick-create tabs. A candidate list is
 * the AI tab's ambiguous case — offer the choice, never pick — otherwise the field gets its
 * normal control, each backed by the same picker the task detail uses so a value the board
 * lacks (a status, a tag) can be created without leaving.
 */
export function TaskFieldEditor({
  field,
  candidates,
  control,
  options,
  projectId,
  boardId,
  projectStatuses,
  selectedTags,
  onTagsChange,
  testIdPrefix = 'ai-proposal-field',
  pending,
}: TaskFieldEditorProps) {
  const { t } = useTranslation('ai');

  if (candidates.length > 1) {
    return (
      <TaskFieldCandidateSelect
        field={field}
        candidates={candidates}
        control={control}
        testIdPrefix={testIdPrefix}
      />
    );
  }

  switch (field) {
    case 'priority': {
      const priorityOptions = AI_PRIORITIES.map((priority) => ({
        value: priority,
        label: t(`quickCreate.priorities.${priority}`),
      }));
      return (
        <FormFieldSelect
          control={control}
          name="priority"
          label={t('quickCreate.fieldLabels.priority')}
          options={priorityOptions}
          placeholder={t('quickCreate.selectOption')}
          data-testid={`${testIdPrefix}-priority-select`}
        />
      );
    }
    // DateOnlyPicker, not FormFieldDatePicker: the draft schema types both dates as
    // `yyyy-MM-dd` (zPlainDate) while that picker emits a UTC ISO instant, so picking a date
    // failed validation and handleSubmit swallowed the confirm with nothing on screen.
    case 'dueDate':
    case 'startDate':
      return (
        <Controller
          name={field}
          control={control}
          render={({ field: dateField, fieldState }) => (
            <DateOnlyPicker
              id={field}
              label={t(`quickCreate.fieldLabels.${field}`)}
              value={(dateField.value as string | null) ?? null}
              onChange={dateField.onChange}
              invalid={fieldState.invalid}
              data-testid={`${testIdPrefix}-${field}-picker`}
            />
          )}
        />
      );
    case 'estimatedMinutes':
      // The board's own picker: hours + minutes with the same presets the task detail offers,
      // so an estimate reads and edits identically wherever it is set.
      return (
        <Controller
          name="estimatedMinutes"
          control={control}
          render={({ field: estimateField }) => (
            <EstimatedPicker
              value={(estimateField.value as number | null) ?? null}
              onSave={(minutes) => estimateField.onChange(minutes)}
            />
          )}
        />
      );
    case 'groupId':
      return (
        <FormFieldSelect
          control={control}
          name="groupId"
          label={t('quickCreate.fieldLabels.groupId')}
          // The pending group is offered as an option of its own, keyed by the name the draft
          // holds: the list is of groups that exist, so without it the select opened on the
          // placeholder while the pill named the group being created.
          options={[
            ...(pending ? [{ value: pending.label, label: pending.label }] : []),
            ...options.groups.map((group) => ({ value: group.id, label: group.label })),
          ]}
          placeholder={t('quickCreate.selectOption')}
          data-testid={`${testIdPrefix}-groupId-select`}
        />
      );
    case 'assigneeId':
      return (
        <FormFieldSelect
          control={control}
          name="assigneeId"
          label={t('quickCreate.fieldLabels.assigneeId')}
          options={options.members.map((member) => ({
            value: member.id,
            label: member.label,
          }))}
          placeholder={t('quickCreate.selectOption')}
          data-testid={`${testIdPrefix}-assigneeId-select`}
        />
      );
    case 'statusId':
      return (
        <TaskFieldStatusEditor
          control={control}
          boardId={boardId}
          projectStatuses={projectStatuses}
          pending={pending}
          testIdPrefix={testIdPrefix}
        />
      );
    case 'tags':
      return (
        <TaskFieldTagsEditor
          control={control}
          projectId={projectId}
          selectedTags={selectedTags}
          onTagsChange={onTagsChange}
        />
      );
    default:
      return (
        <FormFieldTextarea
          control={control}
          name="description"
          label={t('quickCreate.fieldLabels.description')}
          rows={3}
          data-testid={`${testIdPrefix}-description-input`}
        />
      );
  }
}
